import React from "react";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Users, Award, Clock } from "lucide-react";
import { services, process } from "../data/constants";
import { usePage } from "../hooks/useApi";
import { pageApi } from "../services/api";

const ServicesPage: React.FC = () => {
  const { data: page, isLoading, error } = usePage("services");

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#FF5A1F] mx-auto mb-4"></div>
          <p className="text-[#334155]">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">
            Error loading page: {error.message}
          </p>
          <p className="text-[#334155]">Using fallback content...</p>
        </div>
      </div>
    );
  }

  // Helper function to get field value with fallback
  const getField = (key: string, fallback: string = ""): string => {
    if (!page) return fallback;
    const fields = pageApi.getPageFields(page);
    return fields[key] || fallback;
  };
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>
          Our Services - Rolleston Homes | Custom Home Building Services
        </title>
        <meta
          name="description"
          content="Comprehensive home building services including custom design, construction, and project management. Quality craftsmanship and personalized service across Canterbury."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
            {getField("hero_title", "Our Services")}
          </h1>
          <p className="text-lg text-[#334155] max-w-3xl mx-auto">
            {getField(
              "hero_subtitle",
              "From initial concept to final completion, we provide comprehensive home building services that ensure your dream home becomes a reality. Our experienced team guides you through every step of the process."
            )}
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-200"
              >
                <div className="w-16 h-16 bg-[#FF5A1F]/10 rounded-full flex items-center justify-center mb-6">
                  <service.icon className="w-8 h-8 text-[#FF5A1F]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1F2937] mb-4">
                  {service.title}
                </h3>
                <p className="text-[#334155] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="w-5 h-5 text-[#16A34A] mt-0.5 flex-shrink-0" />
                      <span className="text-[#334155] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("process_title", "Our Building Process")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "process_subtitle",
                "We follow a proven process that ensures quality results and keeps you informed every step of the way."
              )}
            </p>
          </div>

          <div className="space-y-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row items-start space-y-4 md:space-y-0 md:space-x-8"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[#FF5A1F] text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#1F2937] mb-2">
                    {item.title}
                  </h3>
                  <p className="text-[#334155] leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#1F2937] mb-6">
              {getField("why_choose_title", "Why Choose Rolleston Homes?")}
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {getField(
                "why_choose_subtitle",
                "We're committed to delivering exceptional results that exceed your expectations and stand the test of time."
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF5A1F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-[#FF5A1F]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-2">
                {getField("why1_title", "Quality Craftsmanship")}
              </h3>
              <p className="text-[#334155]">
                {getField(
                  "why1_description",
                  "Over 15 years of experience delivering exceptional homes with attention to detail and quality materials."
                )}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF5A1F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-[#FF5A1F]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-2">
                {getField("why2_title", "On-Time Delivery")}
              </h3>
              <p className="text-[#334155]">
                {getField(
                  "why2_description",
                  "98% of our projects are completed on schedule, thanks to our efficient project management and planning."
                )}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#FF5A1F]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#FF5A1F]" />
              </div>
              <h3 className="text-xl font-bold text-[#1F2937] mb-2">
                {getField("why3_title", "Personalized Service")}
              </h3>
              <p className="text-[#334155]">
                {getField(
                  "why3_description",
                  "Dedicated project managers and personalized attention ensure your vision becomes reality."
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1F2937] py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
            {getField("cta_title", "Ready to Start Your Project?")}
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            {getField(
              "cta_subtitle",
              "Let's discuss your vision and create a plan that brings your dream home to life. Contact us today for a free consultation."
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={getField("cta_button1_link", "/contact")}
              className="bg-[#FF5A1F] text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-[#e4501c] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {getField("cta_button1_text", "Get Free Consultation")}
            </a>
            <a
              href={getField("cta_button2_link", "/homes")}
              className="border-2 border-white text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white hover:text-[#1F2937] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {getField("cta_button2_text", "View Our Homes")}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
